import CartlineQuantityBreakPricing from "@bevco/Components/CartlineQuantityBreakPricing";
import MinimumOrderAndMultiplesOfInformation, {
    MinimumOrderAndMultiplesOfInformationStyles,
} from "@bevco/Components/MinimumOrderAndMultiplesOfInformation";
import SpoolSelector from "@bevco/Components/SpoolSelector";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@bevco/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@bevco/Overrides/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@bevco/Overrides/Components/ProductQuantityBreakPricing";
import CartLineNotes from "@bevco/Overrides/Widgets/Cart/CartLineNotes";
import { removeKeysWithPrefixes } from "@bevco/Store/Handlers/Cart/UpdateCartLine";
import { setGA4Event } from "@bevco/Store/Handlers/Utilities/CustomTracking";
import useCustomQuoteRequired from "@bevco/Store/Handlers/Utilities/useCustomQuoteRequired";
import { getLooseEndsText } from "@bevco/Utilities/Utilities";
import { colorStrings } from "@bevco/Utilities/variables";
import useOverlayCartLineBadge, {
    CartLineBadgesProps,
    CartLineCardBadgedStyles,
    useWidgetCartLineBadges,
} from "@bevco/Widgets/VerndaleBox/CartLineBadges";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import {
    CartLineModel,
    ProductSettingsModel,
    ProductSubscriptionDto,
    PromotionModel,
} from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// Verndale Customization - extend CartLineBadgesProps
interface OwnProps extends CartLineBadgesProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    state,
    pageType: getCurrentPage(state).type, // Verndale Customization: Putup price
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles extends CartLineCardBadgedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    // Verndale Customization
    quoteOnly?: TypographyPresentationProps;
    moqLabel?: TypographyPresentationProps;
    moqContainer?: InjectableCss;
    cartLineNotesGridItemServices?: InjectableCss;
    servicesLink?: LinkPresentationProps;
    allowLooseEndsLabel?: TypographyPresentationProps;
    quoteOnlyBadge?: InjectableCss;
    quantityDiscounts?: ProductQuantityBreakPricingStyles;
    qtyLabel?: TypographyPresentationProps;
    cartLineServicesGridItem?: GridItemProps;
    minOrderAndMultiplesOf?: MinimumOrderAndMultiplesOfInformationStyles;
    customLineSubtotalWrapper?: InjectableCss;
    customerPriceLabel?: InjectableCss;
    putupPriceQtyGridItem?: GridItemProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 7, 7] },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: { width: 12 },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityGridItem: {
        width: 6,
    },
    extendedUnitNetPriceGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    addToListLink: {
        css: css`
            margin-top: 20px;
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: { width: 12 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },

    binInformationsGridItem: {
        width: 12,
    },

    // Verndale Customization Start - Putup Pricing
    customLineSubtotalWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            width: 100%;
            margin-top: 10px;
            padding-top: 10px;
        `,
    },

    customerPriceLabel: {
        css: css`
            background-color: ${getColor(colorStrings.successMain)};
            color: ${getColor(colorStrings.commonGlobalWhite)};
            padding: 8px 10px;
            text-align: center;
            font-size: 14px;
            font-weight: 400;
            margin-top: 10px;
        `,
    },

    putupPriceQtyGridItem: {
        width: 12,
        css: css`
            &.putup-price {
                padding-bottom: 10px;

                & > div {
                    width: 100%;
                }

                & + div {
                    padding-top: 0;
                }

                .customUOM {
                    top: 59% !important;
                }
            }
        `,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    // Verndale Customization
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    pageType,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);
    const [servicesChecked, setServicesChecked] = useState(false);
    const [looseEndsChecked, setLooseEndsChecked] = useState(false);

    // Verndale Customization Start - Putup Pricing
    const [numberOfSpools, setNumberOfSpools] = useState([1]);
    const [selectedNumberOfSpools, setSelectedNumberOfSpools] = useState(cartLine.properties["spoolSelected"] ?? "1");
    const [spoolsLength, setSpoolsLength] = useState(0);

    useEffect(() => {
        if (cartLine.pricing && cartLine.qtyOrdered && cartLine.pricing?.additionalResults?.spools) {
            setNumberOfSpools(getNumberOfSpools(cartLine.pricing?.additionalResults?.spools));
            setSpoolsLength(getSpoolsLength(cartLine.qtyOrdered, selectedNumberOfSpools));
        }
    }, [cartLine.pricing]);

    useEffect(() => {
        if (!cartLine.qtyOrdered) {
            return;
        }

        if (!cartLine.properties["spoolSelected"]) {
            return;
        }

        setSpoolsLength(getSpoolsLength(cartLine.qtyOrdered, selectedNumberOfSpools));
        const updatedCartLine = updatePutUpCartLine(cartLine, cartLine.qtyOrdered, selectedNumberOfSpools);

        if (selectedNumberOfSpools !== cartLine.properties["spoolSelected"]) {
            updateCartLine({
                shouldNotLoadFullCart: true,
                shouldNotReloadPromotions: true,
                cartLine: {
                    ...updatedCartLine,
                    qtyOrdered: cartLine.qtyOrdered,
                },
            });
        }
    }, [selectedNumberOfSpools]);

    const getNumberOfSpools = (value: string) => {
        return JSON.parse(value);
    };

    const getSpoolsLength = (qty: number, selectedNumberOfSpools: string) => {
        const selectedSpools = parseInt(selectedNumberOfSpools, 10);
        return qty / selectedSpools;
    };
    // Verndale Customization End - Putup Pricing

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                // Verndale Customization - Update the putup pricing when the qty ordered changes,
                // if user changes qty, selected number of spools is 1
                const updatedCartLine = isPutUpPrice ? updatePutUpCartLine(cartLine, qtyOrdered, "1") : cartLine;

                updateCartLine({
                    cartLine: {
                        ...updatedCartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;

        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    // Verndale Customization -- We add the services notes to a custom property called services
    const servicesNotesChangeHandler = (notes: string) => {
        if (notes !== cartLine.properties["services"]) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        services: notes,
                        isQuote: servicesChecked ? "true" : "false",
                    },
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    // Verndale Customization -- We add the loose ends to a custom property called looseEnds
    const looseEndsChangeHandler = () => {
        setLooseEndsChecked(!looseEndsChecked);
        setTimeout(() => {
            const updatedProperties = {
                ...removeKeysWithPrefixes(cartLine.properties, ["isc_", "product_"]),
                looseEnds: looseEndsChecked ? "No" : "Yes",
            };

            // Create a temporary cart line object with updated properties to get the correct loose ends text
            const tempCartLine = {
                ...cartLine,
                properties: updatedProperties,
            };

            const stringsToRemove = ["Never Loose Ends", "Allow Loose Ends-YES", "Allow Loose Ends-NO"];
            const pattern = new RegExp(stringsToRemove.join("|"), "g");
            const looseEndsText = getLooseEndsText(tempCartLine);
            const updatedNotes = `${(cartLine.notes || "").replace(pattern, "")} ${looseEndsText}`.trim();

            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: updatedProperties,
                    // TODO: Remove the comment below, if client wants to actually append the looseEndsText when clicking the checkbox the checkbox Allow Loose Ends-YES or Allow Loose Ends-NO
                    // notes: updatedNotes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Cart Line Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }, 300);
    };

    // Verndale Customization -- We change the status of the cart line to quote only
    const servicesChangeHandler = () => {
        setServicesChecked(!servicesChecked);
        setTimeout(() => {
            const stringsToRemove = ["Never Loose Ends", "Allow Loose Ends-YES", "Allow Loose Ends-NO"];
            const pattern = new RegExp(stringsToRemove.join("|"), "g");
            const looseEndsText = getLooseEndsText(cartLine);
            const updatedNotes = `${(cartLine.notes || "").replace(pattern, "")} ${looseEndsText}`.trim();
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...removeKeysWithPrefixes(cartLine.properties, ["isc_", "product_"]),
                        isQuotedByUser: servicesChecked ? "false" : "true",
                        addServicesSelected: servicesChecked ? "false" : "true",
                        services: servicesChecked ? "" : cartLine.properties["services"],
                    },
                    notes: !servicesChecked ? updatedNotes : "",
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Cart Line Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }, 300);
    };

    // Verndale Customization -- Define the function to update the cart line putup pricing
    const updatePutUpCartLine = (
        cartLine: CartLineModel,
        qtyOrdered: number,
        selectedNumberOfSpools: string,
    ): CartLineModel => {
        // Calculate the division result
        const divisionResult = qtyOrdered / +selectedNumberOfSpools;
        // Check if the division result is an integer
        const spoolLength = Number.isInteger(divisionResult) ? divisionResult : qtyOrdered;
        const spoolSelected = Number.isInteger(divisionResult) ? selectedNumberOfSpools : 1;

        return {
            ...cartLine,
            properties: {
                ...cartLine.properties,
                spoolLength: spoolLength.toString(),
                spoolSelected: spoolSelected.toString(),
            },
        } as CartLineModel;
    };

    // END Verndale Customization

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }
        setGA4Event([], "remove_item", {});
        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    // Verndale Customization - Setting the initial state of the loose ends checkbox
    useEffect(() => {
        // eslint-disable-next-line camelcase
        const {
            properties: { looseEnds, isc_looseEnds: iscLooseEnds, addServicesSelected, allowLooseEnds, isBackOrder },
        } = cartLine;

        if (
            iscLooseEnds === "" &&
            looseEnds === "Yes" &&
            allowLooseEnds === "true" &&
            (isBackOrder === "false" || isBackOrder === "" || isBackOrder === undefined)
        ) {
            setLooseEndsChecked(true);
        } else {
            setLooseEndsChecked(false);
        }

        if (addServicesSelected && addServicesSelected === "true") {
            setServicesChecked(true);
        } else {
            setServicesChecked(false);
        }
    }, []);

    const { showOverlayBadge, overlayBadge, overlayBadgeWrapperStyles } = useOverlayCartLineBadge({
        cartLine,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetCartLineBadges({ cartLine, showTextBadges, showImageBadges });

    /* Verndale Custom Feature - Flags to verify if information should show or not based on CMI or Manufacturer */
    const isCmiProduct = (cartLine.properties["isc_isCmiProduct"] ?? "").toLowerCase() === "true";
    const shouldShowBrand =
        cartLine.brand &&
        !isCmiProduct &&
        (cartLine.properties["manufacturerNameValue"] ?? "").toLowerCase() !== "approved manufacturer";

    // VERNDALE CUSTOMIZATION START - PutUp Pricing
    const allowedPages = ["CartPage"];
    const isCustomerPrice = cartLine.pricing?.additionalResults?.customerPrice === "true";
    const isPutUpPrice =
        cartLine.properties["spoolSelected"] &&
        cartLine.properties["spoolSelected"] !== "" &&
        allowedPages.includes(pageType);
    let customerSpoolLength = 0;
    if (isCmiProduct) {
        customerSpoolLength = Number(cartLine.properties["isc_customerLength"].replace(/,/g, ""));
    }
    // VERNDALE CUSTOMIZATION END - PutUp Pricing

    // Verndale Customization - Back Order
    const isBackOrder = cartLine.properties["isBackOrder"] === "true";
    // END Verndale Customization

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                {/*  Verndale Customization */}
                {!showOverlayBadge && showImage && (
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                )}
                {showOverlayBadge && overlayBadge && showImage && (
                    <StyledWrapper {...styles.badgeAreaWrapper} data-test-selector="productCompareBadge">
                        <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                        <StyledWrapper {...overlayBadgeWrapperStyles}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {/* END VERNDALE CUSTOMIZATION */}
            </GridItem>
            <GridItem {...styles.cartLineInfoGridItem}>
                <GridContainer {...styles.cartLineInfoContainer}>
                    <GridItem {...styles.productInfoGridItem}>
                        <GridContainer {...styles.productInfoContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                {shouldShowBrand && (
                                    <ProductBrand brand={cartLine.brand!} extendedStyles={styles.productBrand} />
                                )}
                                {showProductTitle && (
                                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                                )}
                            </GridItem>
                            {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                <GridItem {...styles.configurationGridItem}>
                                    {cartLine.sectionOptions!.map(option => (
                                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                            {`${option.sectionName}:${option.optionName}`}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}
                            <GridItem {...styles.productPartNumbersGridItem}>
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                    showProductNumber={showProductNumber}
                                    // Verndale Customization
                                    showManufacturerItem={!isCmiProduct}
                                    cmiCustomerNumber={cartLine.properties["isc_cmiCustomer"] ?? ""}
                                    modelNumber={cartLine.properties["product_modelNumber"] ?? ""}
                                    cmiBevcoMaterial={cartLine.properties["isc_cmiBevcoMaterial"] ?? ""}
                                    manufacturerName={cartLine.properties["manufacturerNameValue"] ?? ""}
                                    // END Verndale Customization
                                />
                            </GridItem>
                            {showVmiBinDetails && currentMode === "Vmi" && (
                                <GridItem {...styles.binInformationsGridItem}>
                                    <ProductBinNumbers
                                        locationId={cart.vmiLocationId}
                                        binId={cartLine.vmiBinId}
                                        isCondensed={false}
                                        extendedStyles={styles.productBinNumbers}
                                    />
                                </GridItem>
                            )}
                            {/* Verndale Customization */}
                            {showInventoryAvailability && (
                                <ProductAvailability
                                    productId={cartLine.productId!}
                                    availability={cartLine.availability!}
                                    unitOfMeasure={cartLine.unitOfMeasure}
                                    trackInventory={cartLine.trackInventory}
                                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                    extendedStyles={styles.productAvailability}
                                />
                            )}
                            {/* END Verndale Customization */}
                            {/* Verndale Customization */}
                            {showTextBadges && textBadges && textBadges.length > 0 && (
                                <GridItem {...styles.textBadgeGridItem}>
                                    <ProductTextBadges
                                        badgeList={textBadges}
                                        maxBadges={maximumNumberOfTextBadges ?? 4}
                                        extendedStyles={styles.textBadgeStyles}
                                    />
                                </GridItem>
                            )}
                            {showImageBadges && imageBadges && imageBadges.length > 0 && (
                                <GridItem {...styles.imageBadgeGridItem}>
                                    <ProductImageBadges
                                        badgeList={imageBadges}
                                        maxBadges={maximumNumberOfImageBadges ?? 4}
                                        extendedStyles={styles.imageBadgeStyles}
                                    />
                                </GridItem>
                            )}
                            {/* END Verndale Customization */}
                            {/* Verndale Customization Start - Putup Pricing */}
                            {!isPutUpPrice && (
                                <GridItem {...styles.quantityAndExtendedUnitNetPriceGridItem}>
                                    <GridContainer {...styles.quantityAndExtendedUnitNetPriceContainer}>
                                        <GridItem {...styles.quantityGridItem}>
                                            <CartLineQuantity
                                                cart={cart}
                                                editable={true}
                                                onQtyOrderedChange={qtyOrderedChangeHandler}
                                                extendedStyles={styles.quantity}
                                            />
                                        </GridItem>
                                        {!cartLine.quoteRequired &&
                                            !cart.cartNotPriced &&
                                            cartLine.pricing &&
                                            showPricing && (
                                                <GridItem {...styles.extendedUnitNetPriceGridItem}>
                                                    <Typography {...styles.qtyLabel}>
                                                        {translate("Line Subtotal")}
                                                    </Typography>
                                                    <Typography
                                                        {...styles.extendedUnitNetPriceText}
                                                        data-test-selector="cartline_extendedUnitNetPrice"
                                                    >
                                                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                            ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                            : cartLine.pricing.extendedUnitNetPriceDisplay}
                                                    </Typography>
                                                    {enableVat && (
                                                        <>
                                                            <Typography as="p" {...styles.vatLabelText}>
                                                                {vatPriceDisplay === "DisplayWithVat" ||
                                                                vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                                    ? `${translate("Inc. VAT")} (${
                                                                          cartLine.pricing.vatRate
                                                                      }%)`
                                                                    : translate("Ex. VAT")}
                                                            </Typography>
                                                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                                <>
                                                                    <Typography {...styles.subtotalWithoutVatText}>
                                                                        {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                                    </Typography>
                                                                    <Typography as="p" {...styles.vatLabelText}>
                                                                        {translate("Ex. VAT")}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </GridItem>
                                            )}
                                    </GridContainer>
                                </GridItem>
                            )}
                            {isPutUpPrice && (
                                <GridItem {...styles.quantityAndExtendedUnitNetPriceGridItem}>
                                    <GridContainer {...styles.quantityAndExtendedUnitNetPriceContainer}>
                                        <GridItem
                                            {...styles.putupPriceQtyGridItem}
                                            className={isPutUpPrice ? "putup-price" : ""}
                                        >
                                            <CartLineQuantity
                                                cart={cart}
                                                editable={true}
                                                onQtyOrderedChange={qtyOrderedChangeHandler}
                                                extendedStyles={styles.quantity}
                                            />
                                        </GridItem>
                                        {!useCustomQuoteRequired(cartLine) && (
                                            <GridItem width={12}>
                                                <SpoolSelector
                                                    selectedNumberOfSpools={selectedNumberOfSpools}
                                                    setSelectedNumberOfSpools={setSelectedNumberOfSpools}
                                                    numberOfSpools={numberOfSpools}
                                                    spoolsLength={spoolsLength}
                                                    uom={cartLine.unitOfMeasure}
                                                    qtyOrdered={cartLine.qtyOrdered ?? 1}
                                                    productId={cartLine.id}
                                                    customerSpoolLength={customerSpoolLength}
                                                    isCmiProduct={isCmiProduct}
                                                ></SpoolSelector>
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                </GridItem>
                            )}
                            {errorMessages.length > 0 && (
                                <GridItem {...styles.cartLineErrorMessageGridItem}>
                                    {errorMessages.map((message, index) => (
                                        <Typography
                                            {...styles.cartLineErrorMessageText}
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            data-test-selector="cartline_errorMessage"
                                        >
                                            {message}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}
                            {/* Verndale Customization End - Putup Pricing */}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.productPriceAndAvailabilityGridItem}>
                        {/* Verndale Customization */}
                        <MinimumOrderAndMultiplesOfInformation
                            cartLine={cartLine}
                            showWrapper={true}
                            customUom={cartLine.unitOfMeasure}
                            extendedStyles={styles.minOrderAndMultiplesOf}
                        />

                        {/* END Verndale Customization */}

                        {!cart.cartNotPriced && !cartLine.quoteRequired && (
                            <>
                                <CartlineQuantityBreakPricing extendedStyles={styles.quantityDiscounts} />
                                <ProductPrice
                                    product={cartLine}
                                    currencySymbol={cart.currencySymbol}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    showInvalidPriceMessage={true}
                                    extendedStyles={styles.productPrice}
                                    showLabel={showPriceLabel}
                                    showUnitOfMeasure={showPerUnitPrice}
                                />
                            </>
                        )}

                        {showPricing &&
                            promotions.map(promotion => (
                                <Typography {...styles.promotionNameText} key={promotion.id}>
                                    {promotion.name}
                                </Typography>
                            ))}
                        {showAddToList && !hideAddToList && cartLine.canAddToWishlist && (
                            <Link
                                {...styles.addToListLink}
                                onClick={addToListClickHandler}
                                data-test-selector="cartline_addToList"
                            >
                                {translate("Add to List")}
                            </Link>
                        )}
                        {/* Verndale Customization Start - Putup Pricing */}
                        {!cartLine.quoteRequired &&
                            !cart.cartNotPriced &&
                            cartLine.pricing &&
                            showPricing &&
                            isPutUpPrice && (
                                <StyledWrapper {...styles.customLineSubtotalWrapper}>
                                    <Typography {...styles.qtyLabel}>{translate("Line Subtotal")}</Typography>
                                    <Typography
                                        {...styles.extendedUnitNetPriceText}
                                        data-test-selector="cartline_extendedUnitNetPrice"
                                    >
                                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                            ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                            : cartLine.pricing.extendedUnitNetPriceDisplay}
                                    </Typography>
                                    {enableVat && (
                                        <>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {vatPriceDisplay === "DisplayWithVat" ||
                                                vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                    ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                                    : translate("Ex. VAT")}
                                            </Typography>
                                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <>
                                                    <Typography {...styles.subtotalWithoutVatText}>
                                                        {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                    </Typography>
                                                    <Typography as="p" {...styles.vatLabelText}>
                                                        {translate("Ex. VAT")}
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    )}
                                </StyledWrapper>
                            )}
                        {isCustomerPrice && (
                            <StyledWrapper {...styles.customerPriceLabel}>{translate("Customer Price")}</StyledWrapper>
                        )}
                        {/* Verndale Customization Start - Putup Pricing */}
                        {cartLine.isSubscription && cartLine.productSubscription && (
                            <ProductDeliveryScheduleButton
                                subscription={
                                    cartLine.properties.productSubscription
                                        ? JSON.parse(cartLine.properties.productSubscription)
                                        : cartLine.productSubscription
                                }
                                disabled={
                                    !cart.canModifyOrder ||
                                    cartLine.isPromotionItem ||
                                    !cartLine.qtyOrdered ||
                                    cartLine.qtyOrdered <= 0
                                }
                                onSave={saveScheduleHandler}
                                extendedStyles={styles.deliveryScheduleButton}
                            />
                        )}
                    </GridItem>
                    {cart.showCostCode && !cartLine.isPromotionItem && (
                        <GridItem {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                            {cart.canEditCostCode ? (
                                <Select
                                    value={cartLine.costCode}
                                    onChange={costCodeChangeHandler}
                                    {...styles.costCodeSelect}
                                >
                                    {cart.costCodes?.map(costCode => (
                                        <option key={costCode.costCode} value={costCode.costCode}>
                                            {costCode.description}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                            )}
                        </GridItem>
                    )}

                    {/* Verndale Customization - LOOSE ENDS */}
                    {cartLine.quoteRequired === false &&
                        !isCmiProduct &&
                        !servicesChecked &&
                        cartLine.properties["allowLooseEnds"] === "true" &&
                        !isBackOrder && (
                            <GridItem {...styles.cartLineNotesGridItem}>
                                <StyledWrapper {...styles.cartLineNotesGridItemServices}>
                                    {cartLine.properties["isc_looseEnds"] === "Yes" && (
                                        <Typography {...styles.allowLooseEndsLabel}>
                                            {translate("May Include Loose Ends")}
                                        </Typography>
                                    )}
                                    {cartLine.properties["isc_looseEnds"] === "No" && (
                                        <Typography {...styles.allowLooseEndsLabel}>
                                            {translate("No Loose Ends Allowed")}
                                        </Typography>
                                    )}
                                    {cartLine.properties["isc_looseEnds"] === "" && (
                                        <Checkbox
                                            checked={looseEndsChecked}
                                            onChange={looseEndsChangeHandler}
                                            data-test-selector="cartLine_allowLooseEnds"
                                        >
                                            {translate("Allow Loose Ends")}
                                        </Checkbox>
                                    )}

                                    {siteMessage("LooseEndsLink")}
                                </StyledWrapper>
                            </GridItem>
                        )}
                    {/* END Verndale Customization */}

                    {/* Verndale Customization - SERVICE NOTES */}
                    {cartLine.properties["isc_AvailableServices"] &&
                        !isCmiProduct &&
                        cartLine.properties["isc_AvailableServices"].length > 0 && (
                            <GridItem {...styles.cartLineServicesGridItem}>
                                <StyledWrapper {...styles.cartLineNotesGridItemServices}>
                                    <Checkbox
                                        checked={servicesChecked}
                                        onChange={servicesChangeHandler}
                                        data-test-selector="cartLine_addServices"
                                    >
                                        {translate("Add services to this product")}
                                    </Checkbox>
                                    {siteMessage("CartServiceLink")}
                                    {/* TODO: Remove after prod deply and link + translation */}
                                    {/* <Link {...styles.servicesLink} href={`${translate("Cart Service Url")}`} target="">
                                        {translate("What are services?")}
                                    </Link> */}
                                    {servicesChecked && (
                                        <CartLineNotes
                                            cart={cart}
                                            editable={true}
                                            onNotesChange={servicesNotesChangeHandler}
                                            extendedStyles={styles.cartLineNotes}
                                            expandLineNotes={true}
                                            isServicesNotes={true}
                                        />
                                    )}
                                </StyledWrapper>
                            </GridItem>
                        )}

                    {/* END Verndale Customization */}

                    {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                        <GridItem {...styles.cartLineNotesGridItem}>
                            <CartLineNotes
                                cart={cart}
                                editable={true}
                                onNotesChange={notesChangeHandler}
                                extendedStyles={styles.cartLineNotes}
                            />
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
            <GridItem {...styles.removeCartLineGridItem}>
                {showRemoveAction && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
